import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip'
import { useMe } from '@/features/auth/hooks'
import { usePendingBlocks } from './hooks/useBlocks'
import { BlockTable } from './components/BlockTable'
import { ApproveRejectDialog } from './components/ApproveRejectDialog'
import type { BlockSummary } from './types'

/**
 * AF-128 — approval queue. Approver-only page; rows are pending blocks.
 *
 * Each row exposes inline Approve / Reject actions, but disables them when
 * the block was requested by the current user — the AC is explicit:
 * "Cannot self-approve (UI disables; backend also enforces)".
 */
export function BlockApprovalQueuePage() {
  const { data: me } = useMe()
  const { data, isLoading } = usePendingBlocks()
  const [active, setActive] = useState<{
    block: BlockSummary
    mode: 'approve' | 'reject'
  } | null>(null)

  const blocks = data?.blocks ?? []
  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">Approval queue</h1>
        <p className="text-sm text-muted-fg">
          {blocks.length} pending block{blocks.length === 1 ? '' : 's'} waiting on
          a checker. You can't approve your own requests.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Pending</CardTitle>
        </CardHeader>
        <CardContent>
          <BlockTable
            blocks={blocks}
            isLoading={isLoading}
            emptyMessage="No blocks waiting on approval. The queue stays cleaner if you stay current."
            renderActions={(b) => (
              <RowActions
                block={b}
                isMine={!!b.requested_by && me?.id === b.requested_by.user_id}
                onApprove={() => setActive({ block: b, mode: 'approve' })}
                onReject={() => setActive({ block: b, mode: 'reject' })}
              />
            )}
          />
        </CardContent>
      </Card>

      {active && (
        <ApproveRejectDialog
          open
          onOpenChange={(open) => {
            if (!open) setActive(null)
          }}
          blockId={active.block.id}
          blockNumber={active.block.block_number}
          defaultMode={active.mode}
          onResolved={() => setActive(null)}
        />
      )}
    </div>
  )
}

interface RowActionsProps {
  block: BlockSummary
  isMine: boolean
  onApprove: () => void
  onReject: () => void
}

function RowActions({ isMine, onApprove, onReject }: RowActionsProps) {
  if (isMine) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button size="sm" variant="ghost" disabled>
              Approve
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>You can't approve your own block request.</TooltipContent>
      </Tooltip>
    )
  }
  return (
    <span className="flex items-center gap-1">
      <Button
        size="sm"
        variant="outline"
        onClick={(e) => {
          e.preventDefault()
          onApprove()
        }}
      >
        Approve
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={(e) => {
          e.preventDefault()
          onReject()
        }}
      >
        Reject
      </Button>
    </span>
  )
}
